import React, { Component } from 'react'
import { connect } from 'react-redux'
import DocumentEvents from 'react-document-events'
import { Route, Switch, Redirect } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import * as Sentry from '@sentry/browser'

import { getAppTitle } from 'utils/titles'

import { getApp, getUpgraded, getTrialInformation } from 'ducks/apps'
import { getDatasources, getDefaultDatasource } from 'ducks/apps/datasources'
import { requestData } from 'ducks/editor/objects'
import { fetchCustomActions } from 'ducks/customActions'
import { fetchApiKey } from 'ducks/apikeys'
import { fetchThirdPartyApiKeys } from 'ducks/thirdPartyApiKeys'
import {
  getOrganization,
  setCurrentOrganization,
  getCurrentOrganizationId,
} from 'ducks/organizations'
import {
  ensureLicensesAreLoaded,
  getLicenses,
} from 'ducks/marketplace/licenses'
import { showModal, TERMS_OF_SERVICE_MODAL } from 'ducks/editor/modals'

import EmptyState from 'components/Shared/EmptyState'
import Loading from 'components/Shared/Loading'
import { Panel, PanelSection } from 'components/Shared/Panel'
import TrialWarning from 'components/Shared/TrialWarning'

import DatasourceItem from '../AppDetail/Data/DatasourceItem'

import Canvas from './Canvas'
import Clipboard from './Clipboard'
import LeftPanel from './LeftPanel'
import LeftNav from './LeftNav'
import LibraryLoader from './LibraryLoader'
import { Data, Screens, Marketplace } from './Routes'
import ExternalUsersModal from './ExternalUsersModal'
import ModalWrapper from './ModalWrapper'
import FontsLoader from './FontsLoader'
import IntegrationTrialEndModal from './IntegrationTrialEndModal'

import './Editor.scss'

export { default as EditorNav } from '../Nav'

class Editor extends Component {
  state = {
    error: null,
    librariesReady: false,
  }

  handleWheel = e => {
    if (e.ctrlKey) {
      e.preventDefault()
    }
  }

  handleLibrariesLoad = librariesReady => {
    this.setState({ librariesReady })
  }

  componentDidMount() {
    const {
      match,
      requestData,
      fetchCustomActions,
      app,
      fetchApiKey,
      ensureLicensesAreLoaded,
      fetchThirdPartyApiKeys,
      showModal,
      location,
    } = this.props

    const { appId } = match.params

    requestData(appId)
    fetchCustomActions(appId)
    fetchApiKey(appId)
    ensureLicensesAreLoaded(app?.OrganizationId, appId)
    fetchThirdPartyApiKeys(appId)
    document.body.classList.add('editor-body')

    if (app && app.Organization && !app.Organization.active) {
      document.body.classList.add('editor-app-unpaid')
      this.addedUnpaidClass = true
    }

    if (location.search.includes('terms=')) {
      showModal(TERMS_OF_SERVICE_MODAL)
    }
  }

  componentWillUnmount() {
    document.body.classList.remove('editor-body')
    document.body.classList.remove('editor-app-unpaid')
  }

  componentDidUpdate(prevProps) {
    const {
      app,
      ensureLicensesAreLoaded,
      location,
      showModal,
      organization,
      setCurrentOrganization,
      currentOrganizationId,
    } = this.props

    if (
      app &&
      app.Organization &&
      !app.Organization.active &&
      !this.addedUnpaidClass
    ) {
      document.body.classList.add('editor-app-unpaid')
      this.addedUnpaidClass = true
    }

    if (app && app.OrganizationId !== prevProps?.app?.OrganizationId) {
      ensureLicensesAreLoaded(app?.OrganizationId, app.id)
    }

    if (organization && organization.id !== currentOrganizationId) {
      setCurrentOrganization(organization.id)
    }

    if (prevProps.location.search !== location.search) {
      if (location.search.includes('terms=')) {
        showModal(TERMS_OF_SERVICE_MODAL)
      }
    }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error })

    Sentry.configureScope(scope => {
      Object.keys(errorInfo).forEach(key => {
        scope.setExtra(key, errorInfo[key])
      })
    })

    Sentry.captureException(error)
  }

  render() {
    const { librariesReady, error } = this.state

    const {
      app,
      match,
      children,
      datasource,
      showEndIntegrationTrialModal,
      licenses,
    } = this.props

    const { OrganizationId } = app || false
    const { appId } = match.params

    const title = getAppTitle(app)

    if (error) {
      return (
        <div className="editor-error">
          <EmptyState greedy>
            <h2>Oh no!</h2>
            <p>An error occurred.</p>
            <p>Please reload the page and try again.</p>
          </EmptyState>
          {children}
        </div>
      )
    }

    if (datasource && datasource.type === 'api') {
      if (window.location.pathname.includes('/data')) {
        return <APIData {...this.props} />
      }
    }

    const showExternalUsersModal =
      app &&
      app.externalUsers &&
      app.externalUsers.enabled &&
      !app.externalUsers.setupStatus.complete

    return (
      <div>
        <DocumentTitle title={title} />
        {OrganizationId && (
          <TrialWarning organizationId={OrganizationId} appId={appId} />
        )}
        <div className="editor">
          {app ? (
            <>
              <LibraryLoader
                app={app}
                onLoad={this.handleLibrariesLoad}
                licenses={licenses}
              />
              <FontsLoader app={app} />
            </>
          ) : null}
          <Clipboard appId={match.params.appId} />
          <DocumentEvents capture onWheel={this.handleWheel} passive={false} />
          {librariesReady ? (
            <Canvas appId={appId} />
          ) : (
            <EmptyState className="editor-body-loader">
              <Loading large />
            </EmptyState>
          )}
          <LeftPanel />
          <LeftNav />
          <Routes />
          <ModalWrapper />
          {showEndIntegrationTrialModal ? (
            <Redirect to={`/apps/${appId}/integration-trial-end`} />
          ) : null}
          {showExternalUsersModal ? (
            <Redirect to={`/apps/${appId}/external-users-setup`} />
          ) : null}
        </div>

        {children}
      </div>
    )
  }
}

const mapStateToProps = (state, { match }) => {
  const { appId } = match.params
  const app = getApp(state, appId)
  const { trialState } = getTrialInformation(state, appId)
  const { OrganizationId } = app || {}
  const organization = getOrganization(state, OrganizationId)
  const currentOrganizationId = getCurrentOrganizationId(state)
  const licenses = getLicenses(state, OrganizationId, appId)

  const { seenEndIntegrationTrial } = organization || {}

  const paying = getUpgraded(state, appId)

  return {
    app,
    organization,
    currentOrganizationId,
    datasources: getDatasources(state, appId),
    datasource: getDefaultDatasource(state, appId),
    showEndIntegrationTrialModal:
      !paying &&
      trialState === 'after' &&
      !seenEndIntegrationTrial &&
      organization,
    licenses,
  }
}

export default connect(mapStateToProps, {
  requestData,
  fetchCustomActions,
  fetchApiKey,
  ensureLicensesAreLoaded,
  fetchThirdPartyApiKeys,
  setCurrentOrganization,
  showModal,
})(Editor)

const Routes = () => {
  return (
    <Switch>
      <Route path="/apps/:appId/screens" component={Screens} />
      <Route path="/apps/:appId/data" component={Data} />
      <Route path="/apps/:appId/marketplace" component={Marketplace} />
      <Route
        path="/apps/:appId/external-users-setup"
        component={ExternalUsersModal}
      />
      <Route
        path="/apps/:appId/integration-trial-end"
        component={IntegrationTrialEndModal}
      />
    </Switch>
  )
}

const APIData = props => {
  const { app, datasources, match } = props
  const { appId } = match.params

  const title = getAppTitle(app)

  return (
    <div className="data">
      <LeftNav />
      <DocumentTitle title={title} />
      <Panel className="data-sidebar api">
        <PanelSection>
          <ul className="data-sidebar-datasources">
            {datasources.map(datasource => (
              <DatasourceItem
                key={datasource.id}
                appId={appId}
                datasource={datasource}
                datasourceId={datasource.id}
              />
            ))}
          </ul>
        </PanelSection>
      </Panel>
      <div className="data-body api">
        <Routes />
      </div>
    </div>
  )
}
